import { ThemeSwitcher } from "../../../shared/components/ThemeSwitcher";
import { UiLanguageSwitcher } from "../../../shared/components/UiLanguageSwitcher";
import { AdminAuthPanelModel } from "../../types/admin";
import { AdminFooter } from "../common/AdminFooter";
import { AuthFlowNotice } from "./AuthFlowNotice";
import { ForgotPasswordRequestView } from "./ForgotPasswordRequestView";
import { PasswordResetView } from "./PasswordResetView";
import { SignInForm } from "./SignInForm";
import { SignUpForm } from "./SignUpForm";
import { TwoFactorChallengeView } from "./TwoFactorChallengeView";
import { VerifyEmailView } from "./VerifyEmailView";
import { TranslationKey } from "../../../shared/i18n/i18n";

type AdminAuthPanelProps = {
  model: AdminAuthPanelModel;
  t: (key: TranslationKey) => string;
};

const renderAuthMode = (props: AdminAuthPanelProps) => {
  const { actions, authMode } = props.model;
  if (authMode === "sign-in" || authMode === "organization-invitation-accept" || authMode === "organization-invitation-decline") {
    return (
      <SignInForm
        email={props.model.signInEmail}
        isSubmitting={props.model.isSubmitting}
        onEmailChange={actions.setSignInEmail}
        onOpenForgotPassword={actions.openForgotPassword}
        onOpenSignUp={actions.openSignUp}
        onPasswordChange={actions.setSignInPassword}
        onSubmit={actions.handleSignIn}
        password={props.model.signInPassword}
        t={props.t}
      />
    );
  }
  if (authMode === "two-factor") {
    return (
      <TwoFactorChallengeView
        code={props.model.twoFactorCode}
        isSubmitting={props.model.isSubmitting}
        method={props.model.twoFactorMethod}
        onCodeChange={actions.setTwoFactorCode}
        onSubmit={actions.handleTwoFactor}
        t={props.t}
      />
    );
  }
  if (authMode === "sign-up") {
    return (
      <SignUpForm
        acceptedTerms={props.model.signUpAcceptedTerms}
        email={props.model.signUpEmail}
        isSubmitDisabled={props.model.signUpSubmitDisabled}
        isSubmitting={props.model.isSubmitting}
        onAcceptedTermsChange={actions.setSignUpAcceptedTerms}
        onEmailChange={actions.setSignUpEmail}
        onOpenSignIn={actions.openSignIn}
        onOpenTermsOfUse={actions.openTermsOfUse}
        onPasswordChange={actions.setSignUpPassword}
        onReceiveUpdatesChange={actions.setSignUpReceiveProductUpdates}
        onSubmit={actions.handleSignUp}
        onTurnstileTokenChange={actions.setSignUpTurnstileToken}
        password={props.model.signUpPassword}
        passwordPattern={props.model.passwordPattern}
        passwordRequirements={props.model.passwordRequirements}
        receiveProductUpdates={props.model.signUpReceiveProductUpdates}
        rules={props.model.signUpPasswordRules}
        turnstileResetKey={props.model.signUpTurnstileResetKey}
        turnstileSiteKey={props.model.signUpTurnstileSiteKey}
        turnstileToken={props.model.signUpTurnstileToken}
        t={props.t}
      />
    );
  }
  if (authMode === "forgot-password") {
    return (
      <ForgotPasswordRequestView
        email={props.model.forgotEmail}
        isSubmitting={props.model.isSubmitting}
        onBackToSignIn={actions.openSignIn}
        onEmailChange={actions.setForgotEmail}
        onSubmit={actions.requestPasswordReset}
        onTurnstileTokenChange={actions.setForgotPasswordTurnstileToken}
        t={props.t}
        turnstileResetKey={props.model.forgotPasswordTurnstileResetKey}
        turnstileSiteKey={props.model.forgotPasswordTurnstileSiteKey}
        turnstileToken={props.model.forgotPasswordTurnstileToken}
      />
    );
  }
  if (authMode === "forgot-password-reset") {
    return (
      <PasswordResetView
        isSubmitDisabled={props.model.resetSubmitDisabled}
        isSubmitting={props.model.isSubmitting}
        newPassword={props.model.newPassword}
        onBackToSignIn={actions.openSignIn}
        onNewPasswordChange={actions.setNewPassword}
        onSubmit={actions.resetPasswordWithToken}
        passwordPattern={props.model.passwordPattern}
        passwordRequirements={props.model.passwordRequirements}
        rules={props.model.newPasswordRules}
        t={props.t}
      />
    );
  }
  return (
    <VerifyEmailView
      email={props.model.verifyEmail}
      isSubmitting={props.model.isSubmitting}
      onBackToSignIn={actions.openSignIn}
      onEmailChange={actions.setVerifyEmail}
      onResendVerification={actions.resendVerification}
      onTurnstileTokenChange={actions.setVerifyEmailTurnstileToken}
      t={props.t}
      turnstileResetKey={props.model.verifyEmailTurnstileResetKey}
      turnstileSiteKey={props.model.verifyEmailTurnstileSiteKey}
      turnstileToken={props.model.verifyEmailTurnstileToken}
    />
  );
};

export const AdminAuthPanel = (props: AdminAuthPanelProps) => {
  return (
    <main className="admin-page admin-auth-page">
      <div className="admin-auth-stack">
        <div className="admin-auth-toolbar">
          <UiLanguageSwitcher className="admin-auth-toolbar-button" />
          <ThemeSwitcher className="admin-auth-toolbar-button" />
        </div>
        <div className="admin-panel">
          <section className="admin-card">
            <div className="admin-heading">
              <span
                aria-hidden="true"
                className="admin-heading-logo"
              />
            </div>
            {props.model.flowNotice ? (
              <AuthFlowNotice
                backToSignInLabel={props.t("admin.link.backToSignIn")}
                message={props.model.flowNotice.message}
                onBackToSignIn={props.model.actions.openSignIn}
                showBackToSignIn={props.model.flowNotice.showBackToSignIn}
                tone={props.model.flowNotice.tone}
              />
            ) : (
              renderAuthMode(props)
            )}
            {!props.model.flowNotice && props.model.statusMessage ? (
              <p className={`status-message is-${props.model.statusMessageTone}`}>{props.model.statusMessage}</p>
            ) : null}
          </section>
        </div>
        <AdminFooter t={props.t} />
      </div>
    </main>
  );
};
